import { PageLayout, PageLayouts } from '@/models/book/book.ts'
import Playlist, { PlaylistDoublePage, PlaylistDoublePageRole } from '@/models/playlist.ts'
import PlaylistFileConnection from '@/models/playlistFileConnection.ts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/Button.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { FilesProvider, useFilesContext } from '@/contexts/filesContext.tsx'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import { Album } from '@/models/album.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'
import UnifiedImageSelectionView from '@/components/UnifiedImageSelectionView.tsx'
import { ImageCropCard } from '@/components/shop/ImageCropCard.tsx'
import { CropData } from '@/types/photoCrop.ts'
import {
  IconArrowsDiff,
  IconCamera,
  IconCheck,
  IconCircleChevronDown,
  IconCircleChevronUp,
  IconImageInPicture,
  IconLayout
} from '@tabler/icons-react'
import { placeImageByArea, placeImageBySlot } from '@/pages/BookDesign.page.tsx'

function randomId() {
  let randomId: string
  try {
    randomId = crypto.randomUUID()
  } catch (e) {
    console.log('Err generating random id', e)
    randomId = `${Math.random()}:${Math.random()}:${Math.random()}`
  }

  return randomId
}

interface BookDesignEditorSelection {
  selectedType: null | 'page' | 'img'
  selectedImg: string | null
  selectedPage: string | null
  getImage(book: Playlist): PlaylistFileConnection | null
  setSelection(selectedType: null | 'page' | 'img', selectedImg: string | null, selectedPage: string | null): void
  isSwitchingPositionOf: PlaylistFileConnection | null
  setIsSwitchingPositionOf(file: PlaylistFileConnection | null): void
}

interface BookDesignEditorActions {
  updateConnection(...connection: Array<PlaylistFileConnection>): void
  updateLayout(page: PlaylistDoublePage, layout: PageLayout): void
  movePageToIndex(page: PlaylistDoublePage, targetIndex: number): void
}

function getImageFromSelection(book: Playlist) {
  // @ts-ignore
  const selection: BookDesignEditorSelection = this as BookDesignEditorSelection
  if (selection.selectedType !== 'img' || !selection.selectedImg) {
    return null
  }
  return book.files.find(file => file.id === selection.selectedImg) || null
}

export function BookDesignEditor({ book, setBook }: { book: Playlist, setBook: (book: Playlist) => void }) {
  const [selectedType, setSelectedType] = useState<BookDesignEditorSelection['selectedType']>(null)
  const [selectedImg, setSelectedImg] = useState<BookDesignEditorSelection['selectedImg']>(null)
  const [selectedPage, setSelectedPage] = useState<BookDesignEditorSelection['selectedPage']>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSwitchingPositionOf, setIsSwitchingPositionOf] = useState<PlaylistFileConnection | null>(null)


  function setSelection(selectedType: null | 'page' | 'img', selectedImg: string | null, selectedPage: string | null): void {
    setSelectedType(selectedType)
    setSelectedImg(selectedImg)
    setSelectedPage(selectedPage)
  }

  const sortedByOrderPages = useMemo(() => {
    const pagesCopy = [...book.pages]
    pagesCopy.sort((a, b) => a.order - b.order)
    return pagesCopy
  }, [book.pages]);

  const selection: BookDesignEditorSelection = {
    selectedType,
    selectedImg,
    selectedPage,
    getImage: getImageFromSelection,
    setSelection,
    isSwitchingPositionOf,
    setIsSwitchingPositionOf,
  }

  const actions: BookDesignEditorActions = {
    movePageToIndex(page: PlaylistDoublePage, targetIndex: number) {
      const sortedPages = [...book.pages].sort((a, b) => a.order - b.order)
      const currentIndex = sortedPages.findIndex(p => p.id === page.id)

      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= sortedPages.length) {
        return
      }

      const updatedPages = [...sortedPages]
      updatedPages.splice(currentIndex, 1)
      updatedPages.splice(targetIndex, 0, page)

      const newPages = updatedPages.map((p, index) => ({
        ...p,
        order: index
      }))

      setBook({
        ...book,
        pages: newPages
      })
    },
    updateConnection(...connection: Array<PlaylistFileConnection>) {
      const ids = new Set(connection.map(file => file.id))

      setBook({
        ...book,
        files: book.files.map(file => ids.has(file.id) ? connection.find(con => con.id == file.id)! : file)
      })
    },
    updateLayout(page: PlaylistDoublePage, layout: PageLayout) {
      const pageFiles = book.files.filter(file => file.page == page.id)
      pageFiles.sort((a, b) => a.order - b.order)
      if (layout.slots.length != pageFiles.length) {
        if (layout.slots.length > pageFiles.length) {
          for (let i = pageFiles.length; i < layout.slots.length; i++) {
            pageFiles.push({
              id: randomId(),
              count: 0,
              crop_height: 0,
              crop_rotated: false,
              crop_width: 0,
              crop_x: 0,
              crop_y: 0,
              file: null,
              height: 0,
              order: 0,
              page: page.id,
              rotation: 0,
              width: 0,
              x: 0,
              y: 0
            })
          }
        } else {
          while (pageFiles.length > layout.slots.length) {
            pageFiles.pop()
          }
        }
      }

      for (let i = 0; i < layout.slots.length; i++) {
        const slot = layout.slots[i]
        const file = pageFiles[i]
        const placement = placeImageBySlot(page, slot, file.file)
        file.order = placement.order
        file.rotation = placement.rotation
        file.x = placement.x
        file.y = placement.y
        file.width = placement.width
        file.height = placement.height
        file.crop_x = placement.crop_x
        file.crop_y = placement.crop_y
        file.crop_height = placement.crop_height
        file.crop_width = placement.crop_width
        file.crop_rotated = placement.crop_rotated
      }

      const files = [
        ...book.files.filter(file => file.page != page.id),
        ...pageFiles,
      ]

      setBook({
        ...book,
        pages: book.pages.map(p => p.id == page.id ? {...p, layout_id: layout.id } : p),
        files,
      })
    },
  }



  function onImgPress(connection: PlaylistFileConnection) {
    if (!connection || connection.id == selectedImg) {
      setSelectedImg(null)
      setSelectedPage(null)
      return
    }

    setSelectedType('img')
    setSelectedImg(connection.id)
    setSelectedPage(book.pages.find(page => page.id == connection.page)?.id || null)
  }

  useEffect(() => {
    function onResize() {
      setContainerWidth(containerRef.current!.clientWidth)
    }

    const resizeObserver = new ResizeObserver(onResize)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, []);

  console.log('sellllll', selection.getImage(book))

  console.log('fileeeess', book.files)
  const coverPagesCount = sortedByOrderPages.filter(page => page.role == PlaylistDoublePageRole.COVER).length

  return <div className='flex flex-col gap-5 items-center' ref={containerRef}>
    { containerWidth && sortedByOrderPages.map((page, i) => <BookPageElement actions={actions} coverPagesCount={coverPagesCount} index={i} isLast={i == sortedByOrderPages.length - 1} key={page.id} page={page} width={containerWidth} book={book} onElementClick={onImgPress} selection={selection} />)}
    <ImageSelectionOverlay selection={selection} actions={actions} book={book} />
  </div>
}

function BookPageElement({ actions, page, index, coverPagesCount, isLast, width, book, selection, onElementClick }: { actions: BookDesignEditorActions, page: PlaylistDoublePage, index: number, coverPagesCount: number, isLast: boolean, width: number, book: Playlist, selection: BookDesignEditorSelection, onElementClick: (element: PlaylistFileConnection) => void }): JSX.Element {
  const scale = width / page.width
  const elements = book.files.filter(file => file.page === page.id)

  function onUpClick() {
    actions.movePageToIndex(page, index-1)
  }

  function onDownClick() {
    actions.movePageToIndex(page, index+1)
  }

  return <div>
    <div className='flex flex-row items-center gap-3 mb-1'>
      <h3 className='font-semibold text-lg'>
        { page.role == PlaylistDoublePageRole.COVER && 'Vorder- und Rückseite'}
        { page.role == PlaylistDoublePageRole.PAGE && <>Seite {index+1 - coverPagesCount}</>}
      </h3>
      {
        page.role != PlaylistDoublePageRole.COVER &&
        <div className='ms-auto flex flex-row items-center gap-3'>
          { (index - coverPagesCount != 0) && <div onClick={onUpClick} className='opacity-50 cursor-pointer hover:opacity-75'><IconCircleChevronUp /></div>}
          { !isLast && <div onClick={onDownClick} className='opacity-50 cursor-pointer hover:opacity-75'><IconCircleChevronDown /></div>}
        </div>
      }
    </div>
    <div className='relative bg-[white] border-opacity-25 border-[black] border-2 shadow-xl cursor-pointer' style={{ width: `${page.width * scale}px`, height: `${page.height * scale}px` }}>
      {
        page.role === PlaylistDoublePageRole.PAGE &&
        <div className='w-[2px] bg-[black] opacity-25 h-full left-[50%] absolute shadow translate-x-[-50%]'></div>
      }
      {
        page.role === PlaylistDoublePageRole.COVER && <>
          <div className='w-[2px] bg-[black] opacity-25 h-full absolute shadow translate-x-[-50%]' style={{ left: `${50-page.spine_width/page.width/2*100}%`}} />
          <div className='w-[2px] bg-[black] opacity-25 h-full absolute shadow translate-x-[-50%]' style={{ left: `${50+page.spine_width/page.width/2*100}%`}} />
        </>
      }
      { elements.map(element => <PageElementComp page={page} scale={scale} element={element} key={element.id} selection={selection} onClick={onElementClick} />)}
    </div>
  </div>
}

function PageElementComp({ page, element, selection, onClick }: { page: PlaylistDoublePage, scale: number, element: PlaylistFileConnection, selection: BookDesignEditorSelection, onClick: (element: PlaylistFileConnection) => void }) {
  const onContainerClick = useCallback(() => {
    onClick(element)
  }, [element, onClick]);
  const imgSrc = element.file ? (element.file.thumbnail || element.file.detail || element.file.file) : ''
  const isSwitchTarget = selection.isSwitchingPositionOf && element.id != selection.isSwitchingPositionOf.id

  return <div className={'overflow-hidden absolute bg-[#E0E0E0] cursor-pointer hover:outline hover:outline-2 outline-primary ' + (element.id == selection.selectedImg ? 'outline-[blue] outline outline-4 hover:outline-4 ' : '')} style={{
    top: `${element.y / page.height * 100}%`,
    left: `${element.x / page.width * 100}%`,
    width: `${element.width / page.width * 100}%`,
    height: `${element.height /  page.height * 100}%`,
  }}
              onClick={onContainerClick}
  >
    { imgSrc && <img
      className='absolute max-w-[unset] max-h-[unset]' src={imgSrc}
      alt=""
      crossOrigin='anonymous'
      style={{
        width: `${(100/element.crop_width)*100}%`,
        height: `${(100/element.crop_height)*100}%`,
        transform: `translateX(-${element.crop_x}%) translateY(-${element.crop_y}%`,
      }}
    /> }
    { !imgSrc && <div className='absolute h-full w-full flex justify-center items-center'><IconCamera className='opacity-50' /></div>}
    { isSwitchTarget && <div className='absolute bg-[white] bg-opacity-50 h-full w-full flex justify-center items-center'><IconArrowsDiff className='opacity-50' /></div>}
  </div>
}

function ImageSelectionOverlay({ selection, actions, book }: { selection: BookDesignEditorSelection, actions: BookDesignEditorActions, book: Playlist }) {
  const img = selection.getImage(book)
  const [isSelectingImage, setIsSelectingImage] = useState(false)
  const [isCroppingImage, setIsCroppingImage] = useState(false)
  const [cropState, setCropState] = useState<CropData[string]['crop'] | null>(null)
  const [isSelectingLayout, setIsSelectingLayout] = useState(false)
  const page = selection.selectedPage && book.pages.find(page => page.id === selection.selectedPage)

  useEffect(() => {
    if (selection.isSwitchingPositionOf && img && selection.isSwitchingPositionOf.id != img.id) {
      selection.setIsSwitchingPositionOf(null)

      // @ts-ignore
      function swapFromTo(from: PlaylistFileConnection, to: PlaylistFileConnection): PlaylistFileConnection {
        return {
          ...from,
          page: to.page,
          x: to.x,
          y: to.y,
          width: to.width,
          height: to.height,
          order: to.order,
          rotation: to.rotation,
          ...(from.file ? placeImageByArea(to, from.file) : { crop_x: 0, crop_y: 0, crop_height: 100, crop_width: 100 }),
        }
      }
      actions.updateConnection(
        swapFromTo(selection.isSwitchingPositionOf, img),
        swapFromTo(img, selection.isSwitchingPositionOf),
      )

      // actions.updateConnection({
      //   ...img,
      //   page: isSwitchingPositionOf.page,
      //   ...(img.file ? placeImageByArea(isSwitchingPositionOf, img.file!) : { crop_x: 0, crop_y: 0, crop_height: 100, crop_width: 100 }),
      // })
      //
      // actions.updateConnection({
      //   ...isSwitchingPositionOf,
      //   page: img.page,
      //
      //   ...(isSwitchingPositionOf.file ? placeImageByArea(img, isSwitchingPositionOf.file!) : { crop_x: 0, crop_y: 0, crop_height: 100, crop_width: 100 }),
      // })
    }
  }, [selection, img, actions]);

  function clearSelection() {
    setIsSelectingImage(false)
    setIsCroppingImage(false)
    setCropState(null)
    setIsSelectingLayout(false)
    selection.setIsSwitchingPositionOf(null)
    selection.setSelection(null, null, null)
  }

  function onFileClick(file: AlbumFile) {
    actions.updateConnection({
      ...img!,
      file,
      ...placeImageByArea(img!, file),
    })
    clearSelection()
    return true
  }

  function onConfirmCrop() {
    if (!cropState) {
      return clearSelection()
    }
    const crop = cropState!
    actions.updateConnection({
      ...img!,
      crop_x: crop.x,
      crop_y: crop.y,
      crop_width: crop.width,
      crop_height: crop.height,
    })
    clearSelection()
  }

  function onPageLayoutSelect(page: PlaylistDoublePage, layout: PageLayout) {
    clearSelection()
    actions.updateLayout(page, layout)

  }

  const { isSwitchingPositionOf } = selection

  if (!img) {
    return
  }

  const showChangeImg = !isSwitchingPositionOf && !isSwitchingPositionOf && !isSelectingLayout && !isCroppingImage
  const showCropImg = !isSwitchingPositionOf && img && img.file && !isSelectingImage
  const showChangePosition = img && !isSelectingImage
  const showChangeLayout = !isSwitchingPositionOf && !isSelectingImage && (page && page.role) != PlaylistDoublePageRole.COVER

  return <>
    <div className='fixed bottom-0 left-0 w-full z-50 bg-secondary border-t-4 border-primary shadow-lg'>
      <div className='container px-4 mx-auto py-3 flex items-center gap-3 justify-center flex-col min-h-[4rem] max-h-[50vh]'>
        <div className='flex flex-row gap-3 justify-center flex-wrap'>
          { showChangeImg && <Button variant={isSelectingImage ? 'outline' : undefined} size='sm' color='primary' onClick={() => setIsSelectingImage(!isSelectingImage)}><IconCamera /> Bild ändern{ isSelectingImage ? ' abbrechen' : ''}</Button> }
          { showCropImg && <Button size='sm' color='primary' onClick={() => setIsCroppingImage(!isCroppingImage)}><IconImageInPicture /> Bild zuschneiden</Button> }
          { showChangePosition && <Button variant={isSwitchingPositionOf ? 'outline' : undefined} size='sm' color='primary' onClick={() => selection.isSwitchingPositionOf ? selection.setIsSwitchingPositionOf(null) : selection.setIsSwitchingPositionOf(img)}><IconArrowsDiff /> Position tauschen{ isSwitchingPositionOf ? ' abbrechen' : ''}</Button> }
          { showChangeLayout && <Button size='sm' color='primary' onClick={() => setIsSelectingLayout(!isSelectingLayout)}><IconLayout /> Seiten-Layout ändern</Button> }
        </div>
        <div className='overflow-scroll'>
          { isSelectingImage && <PhotoPickComponent
            initialSelectedImages={book.files.map(con => con.file).filter(file => file != null)}
            onFileClick={onFileClick}
            minImageCount={null}
            maxImageCount={null}
            onContinue={function (): void {

            }} onBack={function (): void {

          }}
          />}
        </div>
      </div>
    </div>
    { isSelectingLayout && page &&
      <div className='fixed top-0 left-0 w-full h-full max-w-full max-h-full z-50 bg-secondary bg-opacity-80 shadow-lg flex justify-center items-center overflow-scroll'>
        <LayoutOptions book={book} page={page} onCancel={() => setIsSelectingLayout(false)} onSelect={onPageLayoutSelect} />
      </div>
    }
    { isCroppingImage && img && img.file &&
      <div className='fixed top-0 left-0 w-full h-full max-w-full max-h-full z-50 bg-secondary bg-opacity-80 shadow-lg flex justify-center items-center'>
        <div className='max-w-[100vw] max-h-full scrollbar-scroll'>
          <ImageCropCard
            customControls={<div className="flex justify-center items-center mb-4">
              <Button color='primary' size='sm' onClick={() => onConfirmCrop()}><IconCheck /> Fertig</Button>
            </div>}
            isEditingExisting={true}
            image={img.file}
            index={0}
            cropData={{
            crop: {
              x: img.crop_x,
              y: img.crop_y,
              width: img.crop_width,
              height: img.crop_height,
            },
            count: 1,
            rotated: img.crop_rotated,
          }} aspectRatio={img.width / img.height} onCropChange={setCropState} onCountChange={() => {}} onToggleRotation={() => actions.updateConnection({
            ...img,
            crop_rotated: img!.crop_rotated,
          })} onRemove={() => {}} />
        </div>
      </div>
    }
  </>
}

function LayoutOptions({ book, page, onCancel, onSelect }: { book: Playlist, page: PlaylistDoublePage, onCancel: () => void, onSelect: (page: PlaylistDoublePage, layout: PageLayout) => void }) {
  const slotCount = book.files.filter(file => file.page == page.id).length || 2

  // Group layouts by slot count
  const layoutsByCount: Record<number, PageLayout[]> = {}
  PageLayouts.forEach(layout => {
    const count = layout.slots.length
    if (!layoutsByCount[count]) {
      layoutsByCount[count] = []
    }
    layoutsByCount[count].push(layout)
  })

  const sortedCounts = Object.keys(layoutsByCount).map(Number).sort((a, b) => {
    // Current slot count first, then ascending order
    if (a === slotCount) return -1
    if (b === slotCount) return 1
    return a - b
  })

  function LayoutPreview({ layout, onSelect }: {layout: PageLayout, onSelect: (layout: PageLayout) => void }) {
    const slots = layout.slots.map(slot => <div className='absolute outline outline-2 outline-[rgba(0,0,0,0.25)] shadow-xl bg-[#E0E0E0] flex justify-center items-center' style={{ top: `${slot.y*100}%`, left: `${slot.x*100}%`, width: `${slot.width*100}%`, height: `${slot.height*100}%` }}>
      <IconCamera className='opacity-50' />
    </div>)
    return <div className='hover:border-primary cursor-pointer relative bg-[white] border-opacity-25 border-[black] border-2 shadow-xl' style={{aspectRatio: page.width / page.height}} onClick={() => onSelect(layout)}>
      <div className='w-[2px] bg-[black] opacity-25 h-full left-[50%] absolute shadow translate-x-[-50%]'></div>
      { slots }
    </div>
  }

  function LayoutGroup({ count, layouts }: { count: number, layouts: PageLayout[], isCurrentCount: boolean }) {
    const headerText = count === 1 ? '1 Bild' : `${count} Bilder`
    return (
      <div>
        <h3 className='font-semibold text-lg mb-3 px-4'>{headerText}</h3>
        <div className='grid grid-cols-2 w-full gap-4 px-4 mb-6'>
          {layouts.map(layout => <LayoutPreview key={layout.id} layout={layout} onSelect={(l) => onSelect(page, l)} />)}
        </div>
      </div>
    )
  }

  return <div className='w-full max-w-[40rem] h-full max-h-full pt-5'>
    <div className='flex justify-center items-center'>
      <Button className='mb-3' color='primary' onClick={onCancel}>Abbrechen</Button>
    </div>
    <div className='w-full overflow-scroll'>
      {sortedCounts.map(count => (
        <LayoutGroup
          key={count}
          count={count}
          layouts={layoutsByCount[count]}
          isCurrentCount={count === slotCount}
        />
      ))}
    </div>
  </div>

}

function PhotoPickComponent({
                                       onContinue,
                                       initialSelectedImages = [],
                                       onBack,
  onFileClick,
                                     }: {
  minImageCount: number | null,
  maxImageCount: number | null,
  onContinue: (selectedImages: AlbumFile[]) => void,
  initialSelectedImages?: AlbumFile[],
  onBack: () => void,
  onFileClick?: (file: AlbumFile) => boolean,
}) {
  const { currentDrive } = useDrives()
  const { currentAlbum: currentOuterAlbum } = useFilesContext()!
  const [currentAlbum, setCurrentAlbum] = useState(currentOuterAlbum!)
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>(initialSelectedImages)

  return (
    <div className="flex flex-col gap-6">
      {/* Combined album selection and image selection */}
      <div className="bg-base-200 p-4 rounded-lg">
        <div className="mb-4">
          <AlbumSelection
            drive={currentDrive!}
            selectedAlbum={currentAlbum!}
            setSelectedAlbum={(album: Album) => setCurrentAlbum(album)}
          />
        </div>

        <FilesProvider albumId={currentAlbum.id} filesFilter={{media: AlbumFileType.IMAGE}}>
          <UnifiedImageSelectionView
            hideOverlay
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            minImageCount={null}
            maxImageCount={null}
            initialSelectedImages={initialSelectedImages}
            onContinue={() => onContinue(selectedImages)}
            onBack={onBack}
            onFileClick={onFileClick}
            hideSelectionButtons
          />
        </FilesProvider>
      </div>
    </div>
  )
}
